import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DashboardLayout from '../components/DashboardLayout';
import { ModelNameWithDetail, RoutedModelDetail } from '../components/ModelNameWithDetail';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const TIER_LABELS = {
  equal: 'Equal',
  better: 'Better',
  cost_effective: 'Cost effective',
  passthrough: 'Passthrough',
};

const DEFAULT_MODEL = 'claude-haiku-4.5';

const REASONING_EFFORT_OPTIONS = ['', 'low', 'medium', 'high'];

const DEFAULT_JSON_SCHEMA = `{
  "type": "object",
  "properties": {
    "answer": { "type": "string" }
  },
  "required": ["answer"],
  "additionalProperties": false
}`;

function buildRequestGenerationParams(generationParams, jsonSchemaText) {
  const params = { ...generationParams };
  const trimmed = jsonSchemaText.trim();
  if (!trimmed) {
    return { params, error: null };
  }
  try {
    const schema = JSON.parse(trimmed);
    params.response_format = {
      type: 'json_schema',
      json_schema: {
        name: 'playground_output',
        strict: true,
        schema,
      },
    };
    return { params, error: null };
  } catch {
    return { params: null, error: 'JSON schema must be valid JSON.' };
  }
}

function countActiveGenerationSettings(generationParams, jsonSchemaText) {
  let count = Object.keys(generationParams).length;
  if (jsonSchemaText.trim()) count += 1;
  return count;
}

const EXAMPLE_PROMPTS = [
  {
    id: 'haiku',
    label: 'Haiku',
    prompt: 'Write me a haiku about open source llms',
  },
  {
    id: 'intro',
    label: 'Intro',
    prompt:
      'Give a short intro about yourself as a model. Who makes you? What are you good for? What are your distinguishing features compared to similar models?',
  },
  {
    id: 'analysis',
    label: 'Analysis',
    prompt: `Analyze this signup data and give a short summary of trends, anomalies, and likely causes.

Daily signups (last 7 days):
Mon 120, Tue 95, Wed 140, Thu 110, Fri 180, Sat 62, Sun 48

By time of day (Fri, our busiest day):
12am-6am 8, 6am-9am 22, 9am-12pm 41, 12pm-3pm 38, 3pm-6pm 35, 6pm-9pm 28, 9pm-12am 8

By time of day (Sun, our quietest day):
12am-6am 3, 6am-9am 6, 9am-12pm 11, 12pm-3pm 10, 3pm-6pm 9, 6pm-9pm 7, 9pm-12am 2

What patterns stand out across weekdays vs weekends, and across times of day?`,
  },
];

const examplePromptButtonClass = (active) =>
  `px-3 py-1.5 rounded-full border text-sm ${
    active
      ? 'border-theme-primary text-theme-primary opacity-100'
      : 'border-dashed border-theme-secondary opacity-70 hover:opacity-100 hover:border-theme-primary'
  }`;

function useClickOutside(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);
  return ref;
}

const formatUsd = (value) => `$${Number(value || 0).toFixed(4)}`;

const formatLatency = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatSavings = (pct) => {
  if (pct == null) return null;
  if (pct >= 1) return `${Math.round(pct)}% cheaper`;
  if (pct <= -1) return `${Math.round(-pct)}% pricier`;
  return 'same price';
};

const formatQuality = (pct) => {
  if (pct == null) return null;
  if (pct > 0) return `+${pct}% quality`;
  if (pct < 0) return `${pct}% quality`;
  return 'same quality';
};

export default function PlaygroundPage() {
  const { selectedHash, selectedProject } = useProject();
  const navigate = useNavigate();
  const [available, setAvailable] = useState([]);
  const [cacheEnabled, setCacheEnabled] = useState(false);
  const [useCache, setUseCache] = useState(true);
  const [useLoadBalancing, setUseLoadBalancing] = useState(true);
  const [prompt, setPrompt] = useState(EXAMPLE_PROMPTS[0].prompt);
  const [activeExampleId, setActiveExampleId] = useState(EXAMPLE_PROMPTS[0].id);
  const [selected, setSelected] = useState([]);
  const [runs, setRuns] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [configId, setConfigId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentOpen, setRecentOpen] = useState(false);
  const [generationParams, setGenerationParams] = useState({});
  const [jsonSchemaText, setJsonSchemaText] = useState('');
  const [schemaError, setSchemaError] = useState('');
  const idRef = useRef(1);
  const resultsScrollRef = useRef(null);
  const cardRefs = useRef({});
  const nextId = () => idRef.current++;

  const scrollRunIntoView = (id) => {
    const scroll = () => {
      const container = resultsScrollRef.current;
      const card = cardRefs.current[id];
      if (!container || !card) return false;
      const top =
        container.scrollTop +
        card.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTo({ top, behavior: 'smooth' });
      return true;
    };
    requestAnimationFrame(() => {
      if (scroll()) return;
      requestAnimationFrame(scroll);
    });
  };

  const refreshRecent = () => {
    if (!selectedHash) return;
    dashboardService.playgroundRecent(
      selectedHash,
      (res) => setRecent(res.data.recent || []),
      console.error,
    );
  };

  const byModel = useMemo(
    () => Object.fromEntries(available.map((m) => [m.input_model, m])),
    [available],
  );

  useEffect(() => {
    if (!selectedHash) return;
    dashboardService.playgroundModels(
      selectedHash,
      (res) => {
        const models = res.data.models || [];
        setAvailable(models);
        setCacheEnabled(res.data.cache_enabled);
        setUseCache(res.data.cache_enabled);
        const first = models.find((m) => m.input_model === DEFAULT_MODEL) || models[0];
        if (first) {
          setSelected([makeEntry(first, nextId())]);
        }
      },
      console.error,
    );
    refreshRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHash]);

  const addEntry = (modelName, tier) => {
    const meta = byModel[modelName];
    if (!meta) return;
    const entry = makeEntry(meta, nextId());
    if (tier) entry.tier = tier;
    setSelected((prev) => [...prev, entry]);
  };

  const addModel = (modelName) => {
    addEntry(modelName);
    setAddOpen(false);
  };

  const loadRecent = (item) => {
    addEntry(item.model, item.tier);
    setPrompt(item.prompt);
    const match = EXAMPLE_PROMPTS.find((p) => p.prompt === item.prompt);
    setActiveExampleId(match?.id ?? null);
    setRecentOpen(false);
  };

  const setPromptText = (text) => {
    setPrompt(text);
    const match = EXAMPLE_PROMPTS.find((p) => p.prompt === text);
    setActiveExampleId(match?.id ?? null);
  };

  const updateSelected = (id, patch) =>
    setSelected((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
    if (configId === id) setConfigId(null);
  };

  const makeRun = (entry, promptText) => ({
    id: nextId(),
    model: entry.model,
    tier: entry.tier,
    status: 'loading',
    entry,
    prompt: promptText,
    mapping: byModel[entry.model]?.tier_options?.[entry.tier] || null,
  });

  const fireRequest = (runId, entry, promptText) => {
    const { params, error } = buildRequestGenerationParams(generationParams, jsonSchemaText);
    if (error) {
      setSchemaError(error);
      updateRun(runId, { status: 'error', error });
      setExpandedIds((prev) => new Set([...prev, runId]));
      scrollRunIntoView(runId);
      return;
    }
    setSchemaError('');
    updateRun(runId, { status: 'loading', streamingContent: '', streamingReasoning: '' });

    dashboardService
      .playgroundStream(
        selectedHash,
        {
          model: entry.model,
          prompt: promptText,
          messages: [{ role: 'user', content: promptText }],
          tier: entry.tier,
          use_cache: useCache,
          use_load_balancing: useLoadBalancing,
          run: true,
          ...params,
        },
        {
          onChunk: (content) => {
            setRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? { ...r, streamingContent: (r.streamingContent || '') + content }
                  : r,
              ),
            );
          },
          onThinking: (content) => {
            setRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? { ...r, streamingReasoning: (r.streamingReasoning || '') + content }
                  : r,
              ),
            );
          },
          onDone: (data) => {
            updateRun(runId, {
              status: 'done',
              result: data,
              streamingContent: '',
              streamingReasoning: '',
            });
            refreshRecent();
          },
          onError: (err) => {
            updateRun(runId, {
              status: 'error',
              error: typeof err === 'string' ? err : err?.response?.data?.detail || 'Request failed',
            });
            setExpandedIds((prev) => new Set([...prev, runId]));
            scrollRunIntoView(runId);
          },
        },
      )
      .catch((err) => {
        updateRun(runId, {
          status: 'error',
          error: err?.message || 'Request failed',
        });
        setExpandedIds((prev) => new Set([...prev, runId]));
        scrollRunIntoView(runId);
      });
  };

  const run = () => {
    if (!selected.length || !prompt.trim()) return;
    const batch = selected.map((entry) => makeRun(entry, prompt));
    setRuns((prev) => [...batch, ...prev]);
    setExpandedIds(new Set([batch[0].id]));
    batch.forEach((r) => fireRequest(r.id, r.entry, r.prompt));
  };

  const retry = (r) => {
    const newRun = makeRun(r.entry, r.prompt);
    setRuns((prev) => [newRun, ...prev]);
    setExpandedIds((prev) => new Set([...prev, newRun.id]));
    scrollRunIntoView(newRun.id);
    fireRequest(newRun.id, newRun.entry, newRun.prompt);
  };

  const updateRun = (id, patch) =>
    setRuns((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const toggleExpand = (id) => {
    const opening = !expandedIds.has(id);
    setExpandedIds((prev) => {
      if (prev.has(id)) {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }
      return new Set([...prev, id]);
    });
    if (opening) scrollRunIntoView(id);
  };

  return (
    <DashboardLayout title="Playground">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-10rem)]">
        {/* Left: models + prompt */}
        <div className="flex flex-col gap-4 min-h-0">
          <div>
            <label className="text-sm opacity-70 mb-2 block">Models</label>
            <div className="flex flex-wrap items-center gap-2">
              {selected.map((entry) => (
                <ModelTag
                  key={entry.id}
                  entry={entry}
                  meta={byModel[entry.model]}
                  isConfiguring={configId === entry.id}
                  onConfigure={() => setConfigId((cur) => (cur === entry.id ? null : entry.id))}
                  onRemove={() => removeSelected(entry.id)}
                  onChange={(patch) => updateSelected(entry.id, patch)}
                />
              ))}
              <AddModel
                models={available}
                open={addOpen}
                onToggle={() => setAddOpen((o) => !o)}
                onPick={addModel}
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <label className="text-sm opacity-70 mb-2">Prompt</label>
            <div className="flex flex-wrap items-center gap-2 mb-2 shrink-0">
              {EXAMPLE_PROMPTS.map((example) => (
                <button
                  key={example.id}
                  type="button"
                  onClick={() => setPromptText(example.prompt)}
                  className={examplePromptButtonClass(activeExampleId === example.id)}
                >
                  {example.label}
                </button>
              ))}
              {recent.length > 0 && (
                <RecentQueries
                  recent={recent}
                  open={recentOpen}
                  onToggle={() => setRecentOpen((o) => !o)}
                  onPick={loadRecent}
                />
              )}
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Type a prompt to compare across models…"
              className="flex-1 bg-theme-secondary border border-theme-secondary rounded-xl p-4 text-theme-text resize-none focus:outline-none focus:border-theme-primary"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <GlobalSettings
              open={globalOpen}
              onToggle={() => setGlobalOpen((o) => !o)}
              cacheEnabled={cacheEnabled}
              useCache={useCache}
              useLoadBalancing={useLoadBalancing}
              onUseCacheChange={setUseCache}
              onLoadBalancingChange={setUseLoadBalancing}
              generationParams={generationParams}
              onGenerationParamsChange={setGenerationParams}
              jsonSchemaText={jsonSchemaText}
              onJsonSchemaTextChange={setJsonSchemaText}
              schemaError={schemaError}
            />
            <button
              type="button"
              onClick={run}
              disabled={!selected.length || !prompt.trim()}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-theme-primary text-theme-button font-medium text-sm disabled:opacity-40 hover:opacity-90"
            >
              <svg aria-hidden className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              {selected.length > 1 ? `Run ${selected.length} models` : 'Run'}
            </button>
          </div>
        </div>

        {/* Right: results accordion */}
        <div ref={resultsScrollRef} className="bg-theme-secondary/20 rounded-xl p-3 overflow-y-auto min-h-0">
          {runs.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="opacity-40 text-sm">Run a prompt to compare model responses.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.id} ref={(el) => { cardRefs.current[r.id] = el; }}>
                  <RunCard
                    run={r}
                    expanded={expandedIds.has(r.id)}
                    onToggle={() => toggleExpand(r.id)}
                    onRetry={() => retry(r)}
                    evalEnabled={selectedProject?.eval_enabled}
                    onRunEval={(usageEventId) => navigate(`/evals/new/?usage_event=${usageEventId}`)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function makeEntry(meta, id) {
  return {
    id,
    model: meta.input_model,
    tier: meta.tier || (meta.available_tiers?.length ? 'equal' : 'passthrough'),
  };
}

function ModelTag({ entry, meta, isConfiguring, onConfigure, onRemove, onChange }) {
  const rootRef = useClickOutside(isConfiguring, onConfigure);
  return (
    <div className="relative" ref={rootRef}>
      <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-theme-secondary/60 border border-theme-secondary text-sm">
        <span className="font-mono text-xs">{entry.model}</span>
        <span className="text-[10px] uppercase tracking-wide opacity-50">{TIER_LABELS[entry.tier]}</span>
        <button
          type="button"
          onClick={onConfigure}
          className="opacity-50 hover:opacity-100 hover:text-theme-primary"
          aria-label="Configure"
        >
          ⚙
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="opacity-50 hover:opacity-100 hover:text-red-500"
          aria-label="Remove"
        >
          ×
        </button>
      </div>
      {isConfiguring && meta && (
        <ConfigPopover entry={entry} meta={meta} onChange={onChange} onClose={onConfigure} />
      )}
    </div>
  );
}

function ConfigPopover({ entry, meta, onChange, onClose }) {
  const preview = meta.tier_options?.[entry.tier];
  const metrics = preview
    ? [formatSavings(preview.savings_pct), formatQuality(preview.quality_delta_pct), preview.tps != null ? `${preview.tps} tps` : null].filter(Boolean)
    : [];
  return (
    <div className="absolute z-30 mt-2 w-72 rounded-xl bg-theme-main border border-theme-secondary p-4 shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium font-mono">{entry.model}</span>
        <button type="button" onClick={onClose} className="opacity-50 hover:opacity-100 text-sm">Done</button>
      </div>

      <label className="block text-xs opacity-60 mb-1">Tier</label>
      {meta.passthrough_only || !meta.available_tiers?.length ? (
        <div className="text-xs opacity-60 mb-3">Direct to OpenRouter — no routing.</div>
      ) : (
        <>
          <select
            value={entry.tier}
            onChange={(e) => onChange({ tier: e.target.value })}
            className="w-full mb-1 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-sm"
          >
            {(meta.available_tiers || []).map((t) => (
              <option key={t} value={t}>{TIER_LABELS[t]}</option>
            ))}
            <option value="passthrough">Passthrough (no routing)</option>
          </select>
          {entry.tier === 'passthrough' ? (
            <div className="text-xs opacity-60 mb-3">Sends to your original provider — no routing.</div>
          ) : (
            <>
              {preview?.os_model && (
                <div className="text-xs opacity-60 font-mono mb-1">{preview.upstream_provider} · {preview.os_model}</div>
              )}
              {metrics.length > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-80 mb-3">
                  {metrics.map((m) => <span key={m}>{m}</span>)}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function Toggle({ label, hint, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div>
        <div className="text-sm">{label}</div>
        {hint && <div className="text-[11px] opacity-50">{hint}</div>}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`shrink-0 w-9 h-5 rounded-full transition-colors ${checked ? 'bg-theme-primary' : 'bg-theme-secondary'} ${disabled ? 'opacity-40' : ''}`}
      >
        <span className={`block w-4 h-4 bg-theme-main rounded-full transform transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

const PROVIDER_ORDER = ['anthropic', 'openai', 'google'];

function AddModel({ models, open, onToggle, onPick }) {
  const closedModels = useMemo(
    () => models.filter((m) => m.provider !== 'open-source'),
    [models],
  );
  const osModels = useMemo(
    () => models.filter((m) => m.provider === 'open-source'),
    [models],
  );
  const groups = useMemo(() => {
    const out = {};
    closedModels.forEach((m) => {
      (out[m.provider] = out[m.provider] || []).push(m);
    });
    return Object.entries(out).sort(
      ([a], [b]) =>
        (PROVIDER_ORDER.indexOf(a) + 1 || 99) - (PROVIDER_ORDER.indexOf(b) + 1 || 99),
    );
  }, [closedModels]);
  const rootRef = useClickOutside(open, onToggle);
  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={onToggle}
        className="px-3 py-1.5 rounded-full border border-dashed border-theme-secondary text-sm opacity-70 hover:opacity-100 hover:border-theme-primary"
      >
        + Add model
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-60 max-h-80 overflow-y-auto rounded-xl bg-theme-main border border-theme-secondary p-2 shadow-xl">
          {groups.map(([provider, items]) => (
            <div key={provider} className="mb-1">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide opacity-40">{provider}</div>
              {items.map((m) => (
                <button
                  key={m.input_model}
                  type="button"
                  onClick={() => onPick(m.input_model)}
                  className="w-full text-left px-2 py-1.5 rounded-lg text-sm font-mono hover:bg-theme-secondary/60"
                >
                  {m.input_model}
                </button>
              ))}
            </div>
          ))}
          {osModels.length > 0 && (
            <div className="mt-2 pt-2 border-t border-theme-secondary/50">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide opacity-40">Open source</div>
              {osModels.map((m) => (
                <button
                  key={m.input_model}
                  type="button"
                  onClick={() => onPick(m.input_model)}
                  className="w-full text-left px-2 py-1.5 rounded-lg text-sm font-mono hover:bg-theme-secondary/60"
                >
                  {m.input_model}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecentQueries({ recent, open, onToggle, onPick }) {
  const rootRef = useClickOutside(open, onToggle);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={onToggle}
        className="px-3 py-1.5 rounded-full border border-dashed border-theme-secondary text-sm opacity-70 hover:opacity-100 hover:border-theme-primary"
      >
        Recent
      </button>
      {open && (
        <div className="absolute top-full mt-2 left-0 z-30 w-80 max-h-96 overflow-y-auto rounded-xl bg-theme-main border border-theme-secondary p-2 shadow-xl">
          <div className="px-2 py-1 text-[10px] uppercase tracking-wide opacity-40">
            Recent playground prompts
          </div>
          {recent.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPick(item)}
              className="w-full text-left px-2 py-2 rounded-lg hover:bg-theme-secondary/60"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs truncate">{item.model}</span>
                <span className="text-[10px] uppercase tracking-wide opacity-50">
                  {TIER_LABELS[item.tier] || item.tier}
                </span>
              </div>
              <div className="text-xs opacity-70 line-clamp-2">{item.prompt}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GlobalSettings({
  open,
  onToggle,
  cacheEnabled,
  useCache,
  useLoadBalancing,
  onUseCacheChange,
  onLoadBalancingChange,
  generationParams,
  onGenerationParamsChange,
  jsonSchemaText,
  onJsonSchemaTextChange,
  schemaError,
}) {
  const rootRef = useClickOutside(open, onToggle);
  const activeParamCount = countActiveGenerationSettings(generationParams, jsonSchemaText);

  const setParam = (key, rawValue) => {
    onGenerationParamsChange((prev) => {
      const next = { ...prev };
      if (rawValue === '' || rawValue == null) {
        delete next[key];
      } else {
        next[key] = rawValue;
      }
      return next;
    });
  };

  const setNumberParam = (key, rawValue) => {
    if (rawValue === '') {
      setParam(key, null);
      return;
    }
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) return;
    setParam(key, parsed);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-theme-secondary text-sm opacity-70 hover:opacity-100 hover:text-theme-primary"
        aria-label="Settings"
      >
        <span aria-hidden>⚙</span>
        <span>
          Settings
          {activeParamCount > 0 && (
            <span className="ml-1 text-theme-primary">({activeParamCount} params)</span>
          )}
        </span>
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 z-30 w-72 rounded-xl bg-theme-main border border-theme-secondary p-4 shadow-xl max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Settings</span>
            <button type="button" onClick={onToggle} className="opacity-50 hover:opacity-100 text-sm">Done</button>
          </div>
          <Toggle
            label="Use cache"
            hint={cacheEnabled ? 'Reuse identical recent responses.' : 'Caching is off for this project.'}
            checked={useCache}
            disabled={!cacheEnabled}
            onChange={onUseCacheChange}
          />
          <Toggle
            label="Load balancing"
            hint="Respect the route % from your routing settings."
            checked={useLoadBalancing}
            onChange={onLoadBalancingChange}
          />
          <div className="border-t border-theme-secondary/50 mt-3 pt-3">
            <div className="text-xs uppercase tracking-wide opacity-50 mb-2">Generation params</div>
            <label className="block text-xs opacity-60 mb-1">Reasoning effort</label>
            <select
              value={generationParams.reasoning_effort ?? ''}
              onChange={(e) => setParam('reasoning_effort', e.target.value || null)}
              className="w-full mb-2 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-sm"
            >
              {REASONING_EFFORT_OPTIONS.map((opt) => (
                <option key={opt || 'default'} value={opt}>
                  {opt ? opt : 'Default'}
                </option>
              ))}
            </select>
            <label className="block text-xs opacity-60 mb-1">Temperature</label>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={generationParams.temperature ?? ''}
              onChange={(e) => setNumberParam('temperature', e.target.value)}
              placeholder="Default"
              className="w-full mb-2 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-sm"
            />
            <label className="block text-xs opacity-60 mb-1">Max tokens</label>
            <input
              type="number"
              min="1"
              step="1"
              value={generationParams.max_tokens ?? ''}
              onChange={(e) => setNumberParam('max_tokens', e.target.value)}
              placeholder="Default"
              className="w-full mb-2 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-sm"
            />
            <label className="block text-xs opacity-60 mb-1">Top P</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={generationParams.top_p ?? ''}
              onChange={(e) => setNumberParam('top_p', e.target.value)}
              placeholder="Default"
              className="w-full mb-2 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-sm"
            />
            <label className="block text-xs opacity-60 mb-1">JSON schema</label>
            <textarea
              value={jsonSchemaText}
              onChange={(e) => onJsonSchemaTextChange(e.target.value)}
              placeholder="Optional. Paste a JSON Schema object for structured output."
              rows={5}
              spellCheck={false}
              className="w-full mb-1 bg-theme-secondary text-theme-text border border-theme-secondary rounded-lg px-2 py-1.5 text-xs font-mono resize-y min-h-[5rem]"
            />
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] opacity-50">Sent as response_format json_schema</span>
              {!jsonSchemaText.trim() && (
                <button
                  type="button"
                  onClick={() => onJsonSchemaTextChange(DEFAULT_JSON_SCHEMA)}
                  className="text-[11px] text-theme-primary hover:opacity-80"
                >
                  Load example
                </button>
              )}
            </div>
            {schemaError && (
              <div className="text-[11px] text-red-500 mb-2">{schemaError}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_META = {
  routed: { label: 'Routed', icon: '⇄', className: 'text-emerald-400' },
  cache_hit: { label: 'Cached', icon: '⚡', className: 'text-sky-400' },
  passthrough: { label: 'Passthrough', icon: '↪', className: 'text-amber-400' },
  error: { label: 'Error', icon: '⚠', className: 'text-red-500' },
};

function RunCard({ run, expanded, onToggle, onRetry, evalEnabled, onRunEval }) {
  const res = run.result;
  const decision = res?.decision;
  const mapping = res?.mapping || run.mapping;
  const originalCost = res ? Number(res.cost_usd || 0) + Number(res.savings_usd || 0) : 0;
  const loading = run.status === 'loading';
  const showStats =
    loading ||
    (run.status === 'done' &&
      (decision === 'routed' ||
        decision === 'cache_hit' ||
        (decision === 'passthrough' && res?.response)));
  const savingsValue =
    res?.savings_usd != null
      ? `${formatUsd(res.savings_usd)}${res.savings_pct != null ? ` (${Math.round(res.savings_pct)}%)` : ''}`
      : '—';

  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-main/40">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-theme-secondary/30 cursor-pointer"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ModelNameWithDetail model={run.model} tier={run.tier} emphasis="original" />
          <span className="text-[10px] uppercase tracking-wide opacity-40 shrink-0">{TIER_LABELS[run.tier]}</span>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          {run.status === 'loading' && (
            <span className="flex items-center gap-1.5 opacity-70">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-theme-primary border-t-transparent animate-spin" />
              Running
            </span>
          )}
          {run.status === 'error' && <span className="text-red-500">⚠ Error</span>}
          {run.status === 'done' && (
            <>
              {decision === 'routed' || decision === 'cache_hit' ? (
                <>
                  {res.savings_pct != null && <Metric>{formatSavings(res.savings_pct)}</Metric>}
                  {mapping?.quality_delta_pct != null && <Metric>{formatQuality(mapping.quality_delta_pct)}</Metric>}
                  <Metric>{formatLatency(res.latency_ms)}</Metric>
                </>
              ) : decision === 'passthrough' && res?.response ? (
                <>
                  <Metric>{formatLatency(res.latency_ms)}</Metric>
                  <Metric>{formatUsd(res.cost_usd)}</Metric>
                </>
              ) : (
                <span className="opacity-50">on your provider</span>
              )}
              <StatusDot decision={decision} />
            </>
          )}
          <span className="opacity-40">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-theme-secondary/50">
          {showStats && (
            <>
              <div className="text-xs opacity-60 mb-3 mt-2 flex flex-wrap items-center gap-1">
                {decision === 'passthrough' ? (
                  <span>served on original model via OpenRouter</span>
                ) : (
                  <>
                    <span>{loading ? 'routing to' : 'routed to'}</span>
                    {mapping?.os_model ? (
                      <RoutedModelDetail compact model={run.model} tier={run.tier} mapping={mapping} />
                    ) : (
                      <span className="font-mono opacity-50">--</span>
                    )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs mb-4">
                <Stat label="Status" value={STATUS_META[decision]?.label} loading={loading} />
                <Stat
                  label="Tokens (in / out)"
                  value={`${res?.tokens_in ?? 0} / ${res?.tokens_out ?? 0}`}
                  loading={loading}
                />
                <Stat label="Latency" value={formatLatency(res?.latency_ms)} loading={loading} />
                {decision === 'passthrough' ? (
                  <>
                    <Stat label="Original cost" value={formatUsd(originalCost)} loading={loading} />
                    <Stat label="New cost" value={formatUsd(res?.cost_usd)} loading={loading} />
                    <Stat label="Savings" value="same price" loading={loading} />
                  </>
                ) : (
                  <>
                    <Stat label="Original cost" value={formatUsd(originalCost)} loading={loading} />
                    <Stat label="New cost" value={formatUsd(res?.cost_usd)} loading={loading} />
                    <Stat label="Savings" value={savingsValue} highlight loading={loading} />
                  </>
                )}
              </div>
              <ResponseBody
                reasoning={loading ? run.streamingReasoning : res.reasoning}
                content={loading ? run.streamingContent : res.response}
                loading={loading}
              />
              {evalEnabled && res?.usage_event_id && (decision === 'routed' || decision === 'cache_hit') && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRunEval?.(res.usage_event_id); }}
                  className="mt-3 px-4 py-1.5 rounded-lg border border-theme-secondary text-sm hover:bg-theme-secondary/30"
                >
                  Run Eval
                </button>
              )}
            </>
          )}
          {run.status === 'error' && (
            <div className="py-2">
              {mapping?.os_model && (
                <div className="text-xs opacity-60 mb-2 flex flex-wrap items-center gap-1">
                  <span>tried routing to</span>
                  <RoutedModelDetail compact model={run.model} tier={run.tier} mapping={mapping} />
                </div>
              )}
              <p className="text-sm text-red-500 mb-3">{run.error}</p>
              <button
                type="button"
                onClick={onRetry}
                className="px-4 py-1.5 rounded-lg bg-theme-primary text-theme-button text-sm font-medium hover:opacity-90"
              >
                Retry
              </button>
            </div>
          )}
          {run.status === 'done' && decision === 'passthrough' && !res?.response && (
            <p className="text-sm opacity-70 py-3">
              Passthrough request completed with no response text.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ThinkingBlock({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const preview = text.replace(/\s+/g, ' ').trim();
  const bodyClass = 'text-sm opacity-60 whitespace-pre-wrap';

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="text-[10px] uppercase tracking-wide opacity-50 mb-1 flex items-center gap-1 hover:opacity-80"
      >
        <span>Thinking</span>
        <span aria-hidden className="opacity-60">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded ? (
        <div className={bodyClass}>{text}</div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`w-full text-left ${bodyClass} line-clamp-3 hover:opacity-80`}
        >
          {preview}
        </button>
      )}
    </div>
  );
}

function ResponseMarkdown({ content }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      className="text-sm opacity-90 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-1 [&_a]:underline [&_code]:bg-theme-secondary/40 [&_code]:rounded [&_code]:px-1 [&_pre]:bg-theme-secondary/40 [&_pre]:rounded [&_pre]:p-2 [&_pre]:overflow-x-auto [&_pre]:mb-2 [&_table]:w-full [&_table]:mb-3 [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-theme-secondary [&_th]:bg-theme-secondary/40 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-medium [&_td]:border [&_td]:border-theme-secondary [&_td]:px-3 [&_td]:py-1.5"
      components={{
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto mb-3">
            <table>{children}</table>
          </div>
        ),
      }}
    >
      {content}
    </Markdown>
  );
}

function ResponseBody({ reasoning, content, loading = false }) {
  if (!reasoning && !content && !loading) return null;
  return (
    <div className="space-y-3">
      {reasoning && <ThinkingBlock text={reasoning} />}
      {(loading || content) && (
        <div>
          <div className="text-[10px] uppercase tracking-wide opacity-50 mb-1 flex items-center gap-1.5">
            <span>Response</span>
            {content && <CopyButton text={content} />}
          </div>
          <div className="text-sm opacity-90">
            {content && <ResponseMarkdown content={content} />}
            {loading && (
              <span className={`inline-flex items-center ${content ? 'mt-2' : ''}`}>
                <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={handleCopy}
        className="opacity-50 hover:opacity-100"
        aria-label="Copy response"
      >
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      </button>
      {copied && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 rounded-md bg-theme-primary text-theme-button text-[10px] font-medium normal-case tracking-normal whitespace-nowrap shadow-lg z-10">
          Copied
        </span>
      )}
    </span>
  );
}

function StatusDot({ decision }) {
  const meta = STATUS_META[decision];
  if (!meta) return null;
  return (
    <span className={`flex items-center gap-1 ${meta.className}`}>
      <span aria-hidden>{meta.icon}</span>
      <span className="font-medium">{meta.label}</span>
    </span>
  );
}

function Metric({ children }) {
  return <span className="opacity-70 hidden sm:inline">{children}</span>;
}

function Stat({ label, value, highlight, loading = false }) {
  return (
    <div>
      <div className="opacity-50 mb-0.5">{label}</div>
      {loading ? (
        <div className="font-mono opacity-50">--</div>
      ) : (
        <div className={`font-mono ${highlight ? 'text-theme-primary' : ''}`}>{value || '—'}</div>
      )}
    </div>
  );
}
